/**
 * Contexte et hook pour le panier d'achat
 * Partagé pour le FrontOffice
 */

import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { Cart, AddToCartInput, UpdateCartItemInput } from '../types/cart';
import { cartApi } from '../api/cart.api';

interface CartContextType {
  cart: Cart | null;
  isLoading: boolean;
  addToCart: (data: AddToCartInput) => Promise<{ success: boolean; error?: string }>;
  updateCartItem: (data: UpdateCartItemInput) => Promise<{ success: boolean; error?: string }>;
  removeFromCart: (articleId: string) => Promise<{ success: boolean; error?: string }>;
  clearCart: () => Promise<{ success: boolean; error?: string }>;
  refreshCart: () => Promise<void>;
  getItemQuantity: (articleId: string) => number;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

interface CartProviderProps {
  children: ReactNode;
}

const EMPTY_CART: Cart = {
  items: [],
  totalHT: 0,
  totalTVA: 0,
  totalTTC: 0,
  itemCount: 0,
};

export function CartProvider({ children }: CartProviderProps) {
  const [cart, setCart] = useState<Cart | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Charger le panier au demarrage
  useEffect(() => {
    loadCart();
  }, []);

  const loadCart = async () => {
    setIsLoading(true);
    
    // Essayer de charger depuis localStorage d'abord
    const savedCart = localStorage.getItem('cart');
    if (savedCart) {
      try {
        setCart(JSON.parse(savedCart));
      } catch (err) {
        console.error('Erreur parsing cart from localStorage:', err);
      }
    }

    // Ensuite charger depuis l'API si connecte
    const token = localStorage.getItem('accessToken');
    if (token) {
      const response = await cartApi.getCart();
      if (response.data?.cart) {
        setCart(response.data.cart);
        localStorage.setItem('cart', JSON.stringify(response.data.cart));
      }
    }

    setIsLoading(false);
  };

  const refreshCart = useCallback(async () => {
    await loadCart();
  }, []);

  const addToCart = useCallback(async (data: AddToCartInput) => {
    const response = await cartApi.addToCart(data);

    if (response.error) {
      return { success: false, error: response.error };
    }

    if (response.data?.cart) {
      setCart(response.data.cart);
      localStorage.setItem('cart', JSON.stringify(response.data.cart));
    }

    return { success: true };
  }, []);

  const updateCartItem = useCallback(async (data: UpdateCartItemInput) => {
    const response = await cartApi.updateCartItem(data);

    if (response.error) {
      return { success: false, error: response.error };
    }

    if (response.data?.cart) {
      setCart(response.data.cart);
      localStorage.setItem('cart', JSON.stringify(response.data.cart));
    }

    return { success: true };
  }, []);

  const removeFromCart = useCallback(async (articleId: string) => {
    const response = await cartApi.removeFromCart(articleId);

    if (response.error) {
      return { success: false, error: response.error };
    }

    if (response.data?.cart) {
      setCart(response.data.cart);
      localStorage.setItem('cart', JSON.stringify(response.data.cart));
    }

    return { success: true };
  }, []);

  const clearCart = useCallback(async () => {
    const response = await cartApi.clearCart();

    if (response.error) {
      return { success: false, error: response.error };
    }

    setCart(EMPTY_CART);
    localStorage.removeItem('cart');
    return { success: true };
  }, []);

  const getItemQuantity = useCallback(
    (articleId: string): number => {
      if (!cart) return 0;
      const item = cart.items.find((i) => i.articleId === articleId);
      return item?.quantity || 0;
    },
    [cart]
  );

  return (
    <CartContext.Provider
      value={{
        cart: cart || EMPTY_CART,
        isLoading,
        addToCart,
        updateCartItem,
        removeFromCart,
        clearCart,
        refreshCart,
        getItemQuantity,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCart(): CartContextType {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}
